import { useDropdown } from '@/components/hooks/useDropdown';
import classNames from '@/utils/classNames';
import ChevronDownIcon from '@bloomberg/web-icons-react/ChevronDown';
import type { HTMLAttributes, ReactNode } from 'react';
import { useRef } from 'react';
import type { FCC } from 'react-fc';
import styles from './DetailsDropdown.module.css';

interface IProps extends HTMLAttributes<HTMLDivElement> {
    label: ReactNode | string;
    onOpened?: () => void;
    closeOnClick?: boolean;
    /** If the label changes based on selection then set this (prevents the box changing size) */
    dynamicLabel?: boolean;
    disabled?: boolean;
}

export const DetailsDropdown: FCC<IProps> = ({
    className,
    label,
    children,
    onOpened,
    disabled,
    closeOnClick = false,
    dynamicLabel = false,
    ...rest
}) => {
    const { buttonRef, popoverRef: innerRef, dropdownId, buttonId, isOpen } = useDropdown({ onOpened, closeOnClick });
    const childrenRef = useRef<HTMLDivElement>(null);

    return (
        <div className={classNames(className, styles.container)} {...rest}>
            <button
                id={buttonId}
                ref={buttonRef}
                type="button"
                disabled={disabled}
                popoverTarget={dropdownId}
                aria-expanded={isOpen}
                title={typeof label === 'string' ? label : undefined}
                className={classNames(styles.button, 'cds-input', 'cds-input--select', { 'cds-disabled': disabled })}
            >
                <span className={styles.labelContainer}>
                    {dynamicLabel ? <span className={styles.labelClip}>{label}</span> : label}
                </span>
                <ChevronDownIcon className={classNames(styles.icon)} />
            </button>
            <div
                id={dropdownId}
                className={styles.popover}
                ref={innerRef}
                popover="auto"
                role="menu"
                aria-labelledby={buttonId}
            >
                <div ref={childrenRef}>{children}</div>
            </div>
        </div>
    );
};
